/* global XMLHttpRequest, FormData */
import { AbortedError, NetworkError, ResponseError, TimeoutError } from 'app/constants/ResponseErrors';
import { getGlobalHistory } from 'app/services/history';
import {
  CancelToken,
  rejectOnCancel,
  throwIfCancelled,
} from 'app/utils/cancelToken';
import { isServerRendering } from 'app/utils/env';
import RequestMonitor from 'app/utils/RequestMonitor';
import { EventEmitter } from 'events';
import { StringifiableRecord, stringify } from 'query-string';
import * as t from 'io-ts';
import logger from 'app/services/logger';
import { createGuid } from 'app/types/guid';

export interface IAuthorizationToken {
  access_token: string;
  expires_in?: number;
  scope?: string;
  token_type: string;
}

export const AuthorizationToken: t.Type<IAuthorizationToken> = t.intersection([
  t.interface({
    access_token: t.string,
    token_type: t.string,
  }),
  t.partial({
    expires_in: t.number,
    scope: t.string,
  }),
]);

export enum RequestHeader {
  Authorization = 'Authorization',
  ContentType = 'Content-Type',
}

type RequestHeaders = {
  [K in RequestHeader]?: string;
};

interface IBaseRequestOptions {
  requestHeaders?: RequestHeaders;
  timeout?: number;
  token?: IAuthorizationToken;
  url: string;
  withCredentials?: boolean;
  cancelToken?: CancelToken,
}

interface IGetRequestOptions extends IBaseRequestOptions {
  data?: StringifiableRecord;
  method: 'GET';
}

interface IPostRequestOptions extends IBaseRequestOptions {
  data?: { [index: string]: unknown } | FormData | undefined;
  method: 'POST';
}

interface IPutRequestOptions extends IBaseRequestOptions {
  data?: { [index: string]: unknown } | FormData | undefined;
  method: 'PUT';
}

interface IDeleteRequestOptions extends IBaseRequestOptions {
  data?: undefined | never;
  method: 'DELETE';
}

export type RequestOptions = IGetRequestOptions | IPostRequestOptions | IPutRequestOptions | IDeleteRequestOptions;

const request = (options: RequestOptions) => {
  const {
    data,
    method,
    requestHeaders = {},
    timeout = 0,
    token,
    url,
    withCredentials = false,
    cancelToken,
  } = options;

  if (isServerRendering() && method !== 'GET') {
    throw new Error(`Tried to make ${method} request to ${url} during server rendering.`);
  }

  const xhr = new XMLHttpRequest();

  // Function to build a ResponseError, in the event an error occurs.
  const buildErrorResponse = (ErrorType: typeof ResponseError) => {
    let response;

    try {
      response = JSON.parse(xhr.responseText);
    } catch (e) {
      response = xhr.responseText;
    }

    const error = new ErrorType({
      data,
      method,
      readyState: xhr.readyState,
      requestHeaders,
      response,
      responseURL: xhr.responseURL,
      status: xhr.status,
      statusText: xhr.statusText,
      // Send non sensitive parts of the token up, if one is provided.
      tokenExpiresIn: token && token.expires_in,
      tokenType: token && token.token_type,
      tokenScope: token && token.scope,
      url,
      withCredentials,
    });

    // Always log 400 errors to console to to make it easier to debug from screenshots
    if (xhr.status === 400) {
      let errorString = `${error}\nURL: ${error.url}\nMethod: ${error.method}`;
      if (error.data) {
        errorString += `\nData: ${JSON.stringify(error.data).slice(0, 500)}`;
      }

      // Response will be a string only when JSON.parse fails above
      if (typeof response === 'string') {
        errorString += `\nResponse: ${response}`;
      } else if (response.error) {
        if (response.error.code === 'DeserializationError.Collection') {
          errorString += `\nCauses:`;
          response.error.causes.forEach((cause: any) => {
            errorString += `\n  Detail: [${cause.code}] ${cause.detail}`;
            errorString += `\n  Nodes: ${JSON.stringify(cause.meta.nodes)}`;
          });
        } else {
          errorString += `\nDetail: [${response.error.code}] ${response.error.detail}`;
          // TODO: switch to optional chaining
          errorString += `\nNodes: ${JSON.stringify(response.error.meta && response.error.meta.nodes)}`;
        }
      }

      logger.error(errorString, error);
    }

    return error;
  };

  const urlWithQueryParams = options.method === 'GET' ? getUrlWithQueryParams(url, options.data) : url;

  const requestId = createGuid();

  const absoluteUrl = getGlobalHistory().getAbsoluteUrl(urlWithQueryParams);

  return (async () => {
    // Do nothing if request is cancelled before starting
    throwIfCancelled(cancelToken);

    try {
      globalRequestEvents.emit(EVENT_REQUEST_START, {
        id: requestId,
        url: absoluteUrl,
        method,
        requestHeaders,
      });

      const promise = new Promise((resolve, reject) => {
        xhr.addEventListener('error', _e => reject(buildErrorResponse(NetworkError)));
        xhr.addEventListener('timeout', () => reject(buildErrorResponse(TimeoutError)));
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              resolve(JSON.parse(xhr.responseText));
            } catch (e) {
              // TODO: Add an option to `request()` for requesting non-JSON content, so we can reject if
              // parsing fails.
              resolve(xhr.responseText);
            }
          } else {
            reject(buildErrorResponse(ResponseError));
          }
        });

        try {
          if (method === 'GET') {
            xhr.open('GET', urlWithQueryParams);
            // Timeout must be set after .open, otherwise IE11 throws InvalidStateError
            xhr.timeout = timeout;
            xhr.withCredentials = withCredentials;
            setHeaders(xhr, requestHeaders, token);
            xhr.send();
          } else {
            xhr.open(method, url);
            xhr.timeout = timeout;
            xhr.withCredentials = withCredentials;
            setHeaders(xhr, requestHeaders, token);

            if (data === undefined) {
              xhr.send();
            } else if (data instanceof FormData) {
              xhr.send(data);
            } else {
              // Add JSON header, since we are going to send the data as JSON.
              xhr.setRequestHeader(RequestHeader.ContentType, 'application/json');
              xhr.send(JSON.stringify(data));
            }
          }

          // abort handler must be registered after xhr.open(), because our Node
          // polyfill for XMLHttpRequest calls xhr.abort() on xhr.open()
          xhr.addEventListener('abort', () => reject(buildErrorResponse(AbortedError)));
        } catch (e) {
          reject(e);
        }
      });

      return await Promise.race([
        promise,
        rejectOnCancel(cancelToken),
      ]);
    } finally {
      globalRequestEvents.emit(EVENT_REQUEST_END, {
        id: requestId,
        url: absoluteUrl,
        method,
        requestHeaders,
        responseHeaders: xhr.getAllResponseHeaders(),
        statusCode: xhr.status,
      });

      // Don't abort if the request is already done. This is necessary for some
      // tests, because .abort() has the side effect of resetting xhr.status to 0,
      // preventing tests from making assertions about the status.
      if (xhr.readyState < XMLHttpRequest.DONE) {
        xhr.abort();
      }
    }
  })();
};

function getUrlWithQueryParams(url: string, data?: StringifiableRecord) {
  if (typeof data === 'object') {
    return `${url}?${stringify(data, { arrayFormat: 'bracket' })}`;
  }

  return url;
}

/**
 * This function will produce a correct value to serialize an undefined or empty array so that the
 * `query-string` module will generate an empty array parameter (param[]=) in the query string.
 * Related discussion here: https://github.com/iknow/eikaiwa_content_frontend/pull/3994#discussion_r481921013
 * PS: I really couldn't come up a better name... Feel free to change it.
 */
export function forceSerializeIfEmptyOrUndefined<T>(arr?: T[]): T[] | [''] {
  return arr && arr.length !== 0 ? arr : [''];
}

const globalRequestEvents = new EventEmitter();

const EVENT_REQUEST_START = 'requeststart';
const EVENT_REQUEST_END = 'requestend';

export function subscribeRequestMonitor(requestMonitor: RequestMonitor) {
  if (!(requestMonitor instanceof RequestMonitor)) {
    throw new Error('subscribeRequestMonitor must take an instance of RequestMonitor');
  }

  const handleRequestStart = (requestInfo: unknown) => requestMonitor.requestStarted(requestInfo);
  const handleRequestEnd = (requestInfo: unknown) => requestMonitor.requestEnded(requestInfo);

  globalRequestEvents.on(EVENT_REQUEST_START, handleRequestStart);
  globalRequestEvents.on(EVENT_REQUEST_END, handleRequestEnd);

  const unsubscribeRequestMonitor = () => {
    globalRequestEvents.removeListener(EVENT_REQUEST_START, handleRequestStart);
    globalRequestEvents.removeListener(EVENT_REQUEST_END, handleRequestEnd);
  };

  return unsubscribeRequestMonitor;
}

/**
 * Helper to apply headers to an instance of XMLHttpRequest.
 */
function setHeaders(xhr: XMLHttpRequest, headers: RequestHeaders = {}, token?: IAuthorizationToken) {
  Object.keys(headers).forEach((header) => {
    const value = headers[header as RequestHeader];
    if (value) {
      xhr.setRequestHeader(header, value);
    }
  });

  if (token) {
    xhr.setRequestHeader(RequestHeader.Authorization, authorizationTokenToHeader(token));
  }
}

/**
 * Creates a value for the 'Authorization' request header.
 */
const authorizationTokenToHeader = ({ access_token, token_type }: IAuthorizationToken) => {
  return `${token_type} ${access_token}`;
};

export const requestToURL = (req: RequestOptions) => {
  // requests don't have precise types, so we can't reasonably expect
  // to rely on the typechecker here.
  if (req.method !== 'GET') {
    throw new Error('GET requests cannot be expressed as URLs');
  }
  return getUrlWithQueryParams(req.url, req.data);
};

export default request;
